import { useCallback, useMemo, useState } from 'react';

import { CopyIcon, EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DeleteConfirmPopoverContent } from '@/components/atoms/DeleteConfirmPopover/DeleteConfirmPopoverContent';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverAnchor } from '@/components/ui/popover';

interface ActionsCellProps {
  insightId: string;
  readonly?: boolean;
  onEdit?: (insightId: string) => void;
  onDuplicate?: (insightId: string) => void;
  onDelete?: (insightId: string) => void;
}

/**
 * Actions cell component for the insights table
 * Provides a dropdown menu with actions like edit, duplicate, and delete
 */
const ActionsCell = ({
  insightId,
  readonly = false,
  onEdit,
  onDuplicate,
  onDelete,
}: ActionsCellProps) => {
  const { t } = useTranslation();
  const [isDeletePopoverOpen, setIsDeletePopoverOpen] = useState(false);

  const handleEdit = useCallback(() => {
    onEdit?.(insightId);
  }, [onEdit, insightId]);

  const handleDuplicate = useCallback(() => {
    onDuplicate?.(insightId);
  }, [onDuplicate, insightId]);

  const handleDeleteConfirm = useCallback(() => {
    onDelete?.(insightId);
    setIsDeletePopoverOpen(false);
  }, [onDelete, insightId]);

  type ActionMenuItem = {
    id: string;
    name: string;
    icon: React.ReactNode;
    onSelect: () => void;
    disabled: boolean;
    variant?: 'default' | 'destructive';
  };

  const actionMenuItems = useMemo(
    (): ActionMenuItem[] => [
      {
        id: 'edit',
        name: t('insights.edit'),
        icon: <PencilIcon size={16} />,
        onSelect: handleEdit,
        disabled: readonly,
      },
      {
        id: 'duplicate',
        name: t('insights.duplicate'),
        icon: <CopyIcon size={16} />,
        onSelect: handleDuplicate,
        disabled: readonly,
      },
    ],
    [t, handleEdit, handleDuplicate, readonly]
  );

  return (
    <Popover open={isDeletePopoverOpen}>
      <DropdownMenu modal={false}>
        <PopoverAnchor asChild>
          <DropdownMenuTrigger asChild>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              variant="ghost-neutral"
              size="icon-sm"
            >
              <EllipsisVerticalIcon size={16} />
            </Button>
          </DropdownMenuTrigger>
        </PopoverAnchor>
        <DropdownMenuContent
          align="end"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {actionMenuItems.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onSelect={item.onSelect}
              disabled={item.disabled}
              variant={item.variant}
            >
              {item.icon}
              {item.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            onSelect={() => setIsDeletePopoverOpen(true)}
            disabled={readonly}
            variant="destructive"
          >
            <Trash2Icon size={16} />
            {`${t('common.delete')}…`}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteConfirmPopoverContent
        title={t('insights.delete_confirm.title')}
        description={t('insights.delete_confirm.description')}
        onCancel={() => setIsDeletePopoverOpen(false)}
        onConfirm={handleDeleteConfirm}
        onPointerDownOutside={() => setIsDeletePopoverOpen(false)}
        onEscapeKeyDown={() => setIsDeletePopoverOpen(false)}
        onClick={(e) => e.stopPropagation()}
      />
    </Popover>
  );
};

export default ActionsCell;
